import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, CalendarDays, Check, ChevronLeft, ChevronRight, Clock3, MapPin, MessageCircle, Scissors, Sparkles, UserRound } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import styles from "../styles/BookingModal.module.css";

const fallbackServiceGroups = [
  { label: "Hair & Locs", icon: Scissors, services: ["Braids & Styling", "Wig Installation / Revamp", "Locs / Micro Locs"] },
  { label: "Grooming", icon: UserRound, services: ["Standard Haircut", "Premium Haircut", "Hair Tinting"] },
  { label: "Beauty", icon: Sparkles, services: ["Nails", "Pedicure / Manicure", "Lashes", "Facial"] },
];
const timeSlots = ["09:00 AM", "11:00 AM", "01:00 PM", "03:00 PM", "05:00 PM", "07:00 PM"];

export default function BookingModal({ onClose, initialService = "" }) {
  const [step, setStep] = useState(initialService ? 1 : 0);
  const [serviceGroups, setServiceGroups] = useState(fallbackServiceGroups);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ client_name:"", phone:"", service:initialService, date:"", time:"", gender:"", send_reminder:true });
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const steps = ["Service", "Date & time", "Your details"];

  useEffect(() => {
    axios.get("/api/services/public").then(({data}) => {
      if (!Array.isArray(data) || !data.length) return;
      const map = new Map();
      data.forEach(s => { if(!map.has(s.category)) map.set(s.category, []); map.get(s.category).push(s.name); });
      const iconFor = cat => /barber/i.test(cat) ? UserRound : /hair|loc|wig|tint/i.test(cat) ? Scissors : Sparkles;
      setServiceGroups(Array.from(map, ([label, services]) => ({label, services, icon: iconFor(label)})));
    }).catch(()=>{});
  }, []);

  const chooseService = (service, group) => {
    const gender = (group?.label || group) === "Grooming" || /barber/i.test(group?.label || group || "") ? "men" : "women";
    setFormData({ ...formData, service, gender });
    setStep(1);
  };

  const handleFinalSubmit = async () => {
    if (!formData.client_name.trim() || !formData.phone.trim()) return toast.error("Please enter your name and WhatsApp number.");
    setLoading(true);
    try {
      let cleanPhone = formData.phone.replace(/\D/g, "");
      if (cleanPhone.startsWith("0")) cleanPhone = "234" + cleanPhone.substring(1);
      await axios.post("/api/admin/create-booking", { ...formData, phone: cleanPhone });
      setFormData({ ...formData, phone: cleanPhone });
      setStep(3);
      toast.success("Appointment request received");
    } catch (err) {
      toast.error("We couldn't complete the booking. Please try again or use WhatsApp.");
    } finally { setLoading(false); }
  };

  const whatsappText = encodeURIComponent(`Hello Dey Fresh Signature, I would like to book ${formData.service || "an appointment"}${formData.date ? ` on ${formData.date}` : ""}${formData.time ? ` at ${formData.time}` : ""}.`);

  return <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label="Book an appointment">
    <Toaster position="top-center" />
    <div className={styles.bookingShell}>
      <aside className={styles.summary}>
        <div className={styles.monogram}>DF</div>
        <div><span className={styles.kicker}>YOUR APPOINTMENT</span><h2>A little time<br/><em>for you.</em></h2><p>Choose your service and preferred time. We’ll take care of the rest.</p></div>
        <div className={styles.summaryDetails}>
          <div><Scissors size={15}/><span><small>SERVICE</small>{formData.service || "Choose a service"}</span></div>
          <div><CalendarDays size={15}/><span><small>DATE & TIME</small>{formData.date ? `${formData.date} · ${formData.time || "Select time"}` : "Choose your time"}</span></div>
          <div><MapPin size={15}/><span><small>LOCATION</small>Shop H9, Ojaja Mall, Ajah</span></div>
        </div>
      </aside>

      <section className={styles.interface}>
        <button className={styles.close} onClick={onClose} aria-label="Close booking"><X size={20}/></button>
        {step < 3 && <div className={styles.progress}>{steps.map((s,i)=><div key={s} className={`${styles.progressItem} ${i <= step ? styles.progressActive : ""}`}><span>{i < step ? <Check size={12}/> : i+1}</span><small>{s}</small></div>)}</div>}

        {step === 0 && <div className={styles.view}>
          <span className={styles.kicker}>STEP 1 OF 3</span><h3>What would you<br/><em>like to book?</em></h3>
          <div className={styles.serviceGroups}>{serviceGroups.map(group => { const Icon=group.icon; return <div className={styles.serviceGroup} key={group.label}><div className={styles.groupTitle}><Icon size={17}/><b>{group.label}</b></div><div className={styles.serviceChoices}>{group.services.map(s=><button key={s} onClick={()=>chooseService(s,group)}>{s}<ChevronRight size={15}/></button>)}</div></div>})}</div>
        </div>}

        {step === 1 && <div className={styles.view}>
          <span className={styles.kicker}>STEP 2 OF 3</span><h3>Choose your<br/><em>preferred time.</em></h3>
          <label className={styles.fieldLabel}>Appointment date</label><input type="date" min={today} value={formData.date} className={styles.dateInput} onChange={e=>setFormData({...formData,date:e.target.value})}/>
          <label className={styles.fieldLabel}>Preferred time</label><div className={styles.timeGrid}>{timeSlots.map(t=><button key={t} className={formData.time===t?styles.selected:""} onClick={()=>setFormData({...formData,time:t})}><Clock3 size={14}/>{t}</button>)}</div>
          <div className={styles.actions}><button className={styles.back} onClick={()=>setStep(0)}><ChevronLeft size={16}/> Back</button><button className={styles.next} disabled={!formData.date||!formData.time} onClick={()=>setStep(2)}>Continue <ChevronRight size={16}/></button></div>
        </div>}

        {step === 2 && <div className={styles.view}>
          <span className={styles.kicker}>STEP 3 OF 3</span><h3>Almost done.<br/><em>How can we reach you?</em></h3>
          <div className={styles.form}><label>Full name<input autoFocus value={formData.client_name} onChange={e=>setFormData({...formData,client_name:e.target.value})} placeholder="Your name"/></label><label>WhatsApp number<input type="tel" value={formData.phone} onChange={e=>setFormData({...formData,phone:e.target.value})} placeholder="e.g. 0916 538 7780"/></label></div>
          <div className={styles.review}><span>{formData.service}</span><b>{formData.date} · {formData.time}</b></div>
          <div className={styles.actions}><button className={styles.back} onClick={()=>setStep(1)}><ChevronLeft size={16}/> Back</button><button className={styles.next} disabled={loading} onClick={handleFinalSubmit}>{loading?"Booking…":"Confirm appointment"}<ChevronRight size={16}/></button></div>
        </div>}

        {step === 3 && <div className={styles.success}>
          <div className={styles.successIcon}><Check size={30}/></div><span className={styles.kicker}>REQUEST RECEIVED</span><h3>Your fresh look<br/><em>is one step closer.</em></h3><p>We’ve received your appointment request for <b>{formData.service}</b>. Dey Fresh will confirm your booking details with you.</p>
          <div className={styles.successSummary}><span>{formData.date}</span><span>{formData.time}</span></div>
          <button className={styles.next} onClick={onClose}>Done</button><a className={styles.whatsapp} href={`https://wa.me/2349165387780?text=${whatsappText}`} target="_blank" rel="noreferrer"><MessageCircle size={16}/> Continue on WhatsApp</a>
        </div>}
      </section>
    </div>
  </div>;
}
